import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import MailAPI from '@/services/api-calls/mail.api';
import type { RootState } from '@/store';

export interface PendingSentEmailEntry {
  id: string;
  // Raw payload as passed to convertToEmailData (utils/emailConverter.ts) -
  // the same shape EmailComposer already builds when it calls
  // sendEmailWithUndo, so no separate field list needs to be maintained here.
  email: Record<string, unknown>;
  tag?: 'sending' | 'failed';
}

interface PendingSentEmailsState {
  entries: Record<string, PendingSentEmailEntry>;
}

const initialState: PendingSentEmailsState = {
  entries: {},
};

// Fetches the canonical row for a pending send and decides whether the
// backend's deferred send has resolved (folder === "sent"), failed
// (deliveryStatus === "failed"), or is still pending. Used both by the
// global "email:updated" socket listener (MailLayout) and by a one-shot
// fallback timeout (EmailComposer) that covers a missed socket event.
export const syncPendingSentEmail = createAsyncThunk(
  'pendingSentEmails/sync',
  async (emailId: string) => {
    const { email } = await MailAPI.getEmail(emailId);
    return { id: emailId, email: email as unknown as Record<string, unknown> };
  },
  {
    // Skip the fetch entirely for ids this slice never tracked (e.g. a
    // synchronous non-undo send emitting email:updated) - the reducer would
    // just no-op on the result anyway, so there's no reason to pay for the
    // GET /email/:id round trip.
    condition: (emailId, { getState }) => {
      const state = getState() as RootState;
      return !!state.pendingSentEmails.entries[emailId];
    },
  }
);

const pendingSentEmailsSlice = createSlice({
  name: 'pendingSentEmails',
  initialState,
  reducers: {
    addPendingSentEmail: (
      state,
      action: PayloadAction<{ id: string; email: Record<string, unknown> }>
    ) => {
      const { id, email } = action.payload;
      state.entries[id] = { id, email, tag: 'sending' };
    },
    failPendingSentEmail: (state, action: PayloadAction<string>) => {
      const entry = state.entries[action.payload];
      if (entry) entry.tag = 'failed';
    },
    removePendingSentEmail: (state, action: PayloadAction<string>) => {
      delete state.entries[action.payload];
    },
    // Called once a real Sent-folder fetch already contains an id this slice
    // is still tracking, so the slice doesn't grow stale entries forever.
    prunePendingSentEmails: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        delete state.entries[id];
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(syncPendingSentEmail.fulfilled, (state, action) => {
      const { id, email } = action.payload;
      const entry = state.entries[id];
      if (!entry) return; // removed already (e.g. user hit Undo) - ignore stale result

      const folder = (email as { folder?: string }).folder;
      const deliveryStatus = (email as { deliveryStatus?: string }).deliveryStatus;

      if (folder === 'sent') {
        state.entries[id] = { id, email };
      } else if (deliveryStatus === 'failed') {
        entry.tag = 'failed';
      }
      // else: still pending - leave tagged "sending". The next real
      // Sent-folder fetch will pick it up once it actually lands.
    });
  },
});

export const {
  addPendingSentEmail,
  failPendingSentEmail,
  removePendingSentEmail,
  prunePendingSentEmails,
} = pendingSentEmailsSlice.actions;
export default pendingSentEmailsSlice.reducer;
